/**
 * SOLID Review
 * S — Strong. One concern: the browser, as the Framework's view models reach it — each role over the page's own resource.
 * O — Good. A new role is one member.
 * L — Strong. Substitutable for any `IConfigDepViewPlatform` (a test's, a corpus bed's).
 * I — Strong. The roles the view models declare; nothing else.
 * D — Good. The composition root: it may name `window`, `history`, `Date` and Web Audio, and it is where the Framework does.
 */
import type {
  IConfigDepViewPlatform, IServiceAnimationFrames, IServiceDeviceMotion, IServiceMediaDevices, IServicePageAddress, IServicePageCursor,
  IServicePageEvents, IServicePageStyles, IServicePageVisibility, IServiceResizeObserver, IServiceViewAds, IServiceViewClipboard,
  IServiceViewClock, IServiceViewDevice, IServiceViewDownloads, IServiceViewLogger, IServiceViewport, IServiceViewTimers,
} from '../viewmodels/platform/IConfigDepViewPlatform';
import { readEnv } from '../../shared/viteEnv';
import { safeReload } from '../../shared/safeReload';

/** Video left open with no play is closed after this long unless the build says otherwise. */
const LIVEKIT_IDLE_TIMEOUT_MS = 2 * 60 * 1000;

/** The two names a browser gives Web Audio's context: the standard one, and older Safari's. */
type TAudioWindow = Window & { readonly AudioContext?: typeof AudioContext; readonly webkitAudioContext?: typeof AudioContext };

/** Chrome's own: the device's memory in GB, rounded to a power of two (min 0.25). */
type TMemoryNavigator = Navigator & { readonly deviceMemory?: number };

/** The ad network's queue on the page: the SDK's object once it loaded, the array it drains until then. */
type TAdsWindow = Window & { adsbygoogle?: { push(slot: object): unknown } };

/** iOS's motion events: the class, and the permission it asks for (13+). */
type TMotionClass = typeof DeviceMotionEvent & { readonly requestPermission?: () => Promise<string> };

/** The build-time Monetag smart link's name. */
const MONETAG_SMART_LINK_KEY = 'VITE_MONETAG_SCRIPT_URL';

/**
 * THE BROWSER FOR THE FRAMEWORK'S VIEW MODELS (frontend-framework-oo P1, 2026-10-29, §28.293) — beside `BrowserPlatform`, which
 * builds the Infrastructure's roles, and over the same page. Every resource is read when a role is asked, never when this is built:
 * the port and UML generators build the composition root under Node, where there is no window.
 */
export class BrowserViewPlatform implements IConfigDepViewPlatform {
  readonly clock: IServiceViewClock = { now: () => Date.now() };

  readonly address: IServicePageAddress = {
    search: () => window.location.search,
    pathname: () => window.location.pathname,
    replace: (url) => history.replaceState(null, '', url),
    push: (state) => history.pushState(state, ''),
    location: () => window.location,
    assign: (url) => { window.location.href = url; },
  };

  readonly timers: IServiceViewTimers = {
    setTimeout: (callback, ms) => setTimeout(callback, ms),
    clearTimeout: (timer) => clearTimeout(timer),
    setInterval: (callback, ms) => setInterval(callback, ms),
    clearInterval: (timer) => clearInterval(timer),
  };

  readonly logger: IServiceViewLogger = { warn: (message, ...details) => console.warn(message, ...details) };

  /** `navigator.mediaDevices`, read when asked: some in-app browsers have none, and the page lists nothing over plain http. */
  readonly mediaDevices: IServiceMediaDevices = {
    available: () => typeof navigator.mediaDevices?.enumerateDevices === 'function',
    list: () => navigator.mediaDevices.enumerateDevices(),
    onChange: (listener) => {
      navigator.mediaDevices.addEventListener?.('devicechange', listener);
      return () => navigator.mediaDevices?.removeEventListener?.('devicechange', listener);
    },
  };

  readonly pageEvents: IServicePageEvents = {
    on: (type, listener) => {
      window.addEventListener(type, listener);
      return () => window.removeEventListener(type, listener);
    },
    onDocument: (type, listener) => {
      document.addEventListener(type, listener);
      return () => document.removeEventListener(type, listener);
    },
  };

  /** The build's value, or the default when it sets none (or nothing a number). */
  readonly liveKitIdleTimeoutMs = Number(readEnv('VITE_LK_IDLE_TIMEOUT_MS')) || LIVEKIT_IDLE_TIMEOUT_MS;

  readonly monetagSmartLink = (): string | undefined => readEnv(MONETAG_SMART_LINK_KEY);

  readonly visibility: IServicePageVisibility = {
    visible: () => document.visibilityState === 'visible',
    onReturn: (listener) => {
      document.addEventListener('visibilitychange', listener);
      window.addEventListener('focus', listener);
      return () => {
        document.removeEventListener('visibilitychange', listener);
        window.removeEventListener('focus', listener);
      };
    },
  };

  readonly reload = (): void => safeReload();

  /**
   * SVG paint-server gradients and CSS animation cause memory pressure on ≤ 1 GB / ≤ 2-core hardware (iPad Air 1, iPad mini 3, older
   * iPhones): those get the flat-colour cups (`welcomeScreen/isLowEndDevice.ts` until P3, evaluated at its import).
   */
  readonly device: IServiceViewDevice = {
    lowEnd: () => {
      const memory = (navigator as TMemoryNavigator).deviceMemory;
      if (memory !== undefined && memory <= 1) return true;
      const cores = navigator.hardwareConcurrency;
      return cores !== undefined && cores <= 2;
    },
    prefersReducedMotion: () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  };

  readonly styles: IServicePageStyles = {
    ensure: (id, css) => {
      if (document.getElementById(id)) return;
      const sheet = document.createElement('style');
      sheet.id = id;
      sheet.textContent = css;
      document.head.appendChild(sheet);
    },
  };

  readonly clipboard: IServiceViewClipboard = { write: (text) => navigator.clipboard.writeText(text) };

  /** Read when asked: the scene asks once, when it is made, as it did at its first render. */
  readonly motion: IServiceDeviceMotion = {
    available: () => typeof DeviceMotionEvent !== 'undefined',
    permissionRequired: () => {
      if (typeof DeviceMotionEvent === 'undefined') return false;
      const motion: TMotionClass = DeviceMotionEvent;
      return typeof motion.requestPermission === 'function';
    },
    requestPermission: () => {
      const motion: TMotionClass = DeviceMotionEvent;
      return typeof motion.requestPermission === 'function' ? motion.requestPermission() : Promise.resolve('unavailable');
    },
    on: (listener) => {
      window.addEventListener('devicemotion', listener);
      return () => window.removeEventListener('devicemotion', listener);
    },
  };

  readonly cursor: IServicePageCursor = { set: (cursor) => { document.body.style.cursor = cursor; } };

  readonly viewport: IServiceViewport = { width: () => window.innerWidth, height: () => window.innerHeight };

  readonly resizeObserver: IServiceResizeObserver = {
    available: () => typeof ResizeObserver !== 'undefined',
    observe: (element, listener) => {
      const observer = new ResizeObserver((entries) => listener(entries));
      observer.observe(element);
      return () => observer.disconnect();
    },
  };

  /**
   * The exact google_ad_code.html pattern: it works whether the async loader already ran (`adsbygoogle` is the SDK's object — the
   * slot fills at once) or not yet (the queue array is made — the SDK drains it when it loads).
   */
  readonly ads: IServiceViewAds = {
    push: () => {
      const page: TAdsWindow = window;
      const queue: object[] = [];
      /** One slot to fill — the network reads nothing from it. */
      const slot: object = {};
      (page.adsbygoogle = page.adsbygoogle || queue).push(slot);
    },
  };

  readonly frames: IServiceAnimationFrames = {
    request: (callback) => requestAnimationFrame(callback),
    cancel: (handle) => cancelAnimationFrame(handle),
  };

  readonly downloads: IServiceViewDownloads = {
    save: (fileName, contents, type) => {
      const file: BlobPropertyBag = { type };
      const url = URL.createObjectURL(new Blob([contents], file));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    },
  };

  readonly audioContext = (): AudioContext => {
    const page = window as TAudioWindow;
    const Context = page.AudioContext ?? page.webkitAudioContext;
    if (Context === undefined) throw new Error('this browser has no Web Audio');
    return new Context();
  };

  readonly random = (): number => Math.random();
}
